import type * as api from '@gitbook/api-client';
import type { DeepPartial } from '@gitbook/util-ts';

/**
 * Generic stream interface for use in agent RPC methods when they want to stream results
 * back to the client via CapnWeb callback passing.
 *
 * TResult must be an object with arbitrary properties representing each chunk of data
 * sent from the DO to the client.
 */
export interface AgentRpcStream<TResult extends object = {}> {
    /**
     * Send a chunk of data to the client.
     */
    onChunk(partialResult: DeepPartial<TResult>);

    /**
     * Signal that the stream has completed.
     */
    onEnd();

    /**
     * Signal that an error occurred during streaming.
     */
    onError(error: { message: string });
}

/**
 * Public Rpc API for any agent.
 */
export type AgentRpc = {
    /**
     * Post a message to the chat.
     */
    postMessage(message: api.AIMessageInput): Promise<void>;

    /**
     * Reset the whole state of the agent.
     */
    reset(): Promise<void>;
};

/**
 * Base input for {@link AgentChangeRequestRpc.generatePageDocumentCompletions}.
 */
interface GeneratePageCompletionsBaseInput {
    /**
     * The ID of the page to generate completions for.
     */
    pageId: string;

    /**
     * The selection range the completion should apply to.
     */
    selection: { type: 'text' | 'block'; anchorKey: string; focusKey: string };

    /**
     * Unique identifier for this completion request.
     */
    completionId: string;

    /**
     * User-provided instructions to guide the completion.
     */
    userInstructions?: string;
}

/**
 * Input for {@link AgentChangeRequestRpc.generatePageDocumentCompletions} in **insert** mode.
 */
interface GeneratePageCompletionsInsertInput extends GeneratePageCompletionsBaseInput {
    mode: 'insert';
}

/**
 * Input for {@link AgentChangeRequestRpc.generatePageDocumentCompletions} in **replace** mode.
 *
 * The completion is streamed through the provided stream callbacks.
 */
interface GeneratePageCompletionsReplaceInput extends GeneratePageCompletionsBaseInput {
    mode: 'replace';
    stream: AgentRpcStream<{ completion: string }>;
}

export type GeneratePageCompletionsInput =
    | GeneratePageCompletionsInsertInput
    | GeneratePageCompletionsReplaceInput;

/**
 * Public Rpc API for the AgentChangeRequest.
 */
export type AgentChangeRequestRpc = {
    /**
     * Generate suggestions for an icon and a description for a page.
     */
    generatePageDescription(input: { pageId: string }): Promise<{
        icon: string;
        description: string;
    }>;

    /**
     * Generate page document completions.
     */
    generatePageDocumentCompletions(input: GeneratePageCompletionsInput): Promise<void>;
};
